import { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectPanels,
  editFloatingTicketPanel,
  closeFloatingTicketPanel,
  selectStartIndexToShow,
  setStartIndexToShow,
  toggleFloatingTicketPanelLoading,
} from 'store/floatingTicketPanelSlice';
import { useQueryClient } from '@tanstack/react-query';
import { setToasterMessage } from 'store/globalSlice';
import { useTheme } from '@mui/styles';
import { showPrompt } from 'store/messagePromptSlice';
import { selectIsNavBarCollapsed } from 'features/loggedInRoutesPanel/slice';
import usePerformanceMeasure from 'common/utils/hooks/usePerformanceMeasure';
import { PERFORMANCE_MEASURE } from 'constants/performanceMeasure';
import { APP_CONSTANTS } from 'constants/index';
import { FloatingTicketPanelInstance } from '../floatingTicketPanelInstance';
import MoreFloatingTickets from '../moreFloatingTickets';
import { FloatingTicketInstanceActions, FloatingTicketInstanceModes } from './modes';
import { StyledFloatingTicketPanelBackdrop } from './style';
import useTexts from '../useTexts';
import { getVisiblePanelsOnScreenCount } from '../utils/utils';
import { CONSTANTS } from '../utils/constants';

const ticketCreatedMessage = (text, ticketId) => text.replace('__id__', ticketId);

export default function FloatingTicketPanelManager() {
  const {
    messageCreatedText,
    viewSR,
    failedToCreateTicket,
    areYouSureBody,
    areYouSureTitle,
    areYouSureCancel,
    areYouSureOk,
  } = useTexts();
  const panels = useSelector(selectPanels);
  const startIndexToShow = useSelector(selectStartIndexToShow);
  const dispatch = useDispatch();
  const keys = Object.keys(panels);
  const isDisabled = keys.some((k) => panels[k].isLoading);
  const theme = useTheme();
  const onShowMoreClicked = (move) => {
    dispatch(setStartIndexToShow(move));
  };
  const queryClient = useQueryClient();
  const { performanceMeasureEventReport } = usePerformanceMeasure();

  const navBarCollapsed = useSelector(selectIsNavBarCollapsed);
  const navBarWidth = navBarCollapsed ? CONSTANTS.NAV_BAR_COLLAPSED_WIDTH : CONSTANTS.NAV_BAR_EXPANDED_WIDTH;

  const handleMessage = useCallback(
    () =>
      window.addEventListener('message', (e) => {
        //message structure is always an object with this structure: {url: ..., action: FloatingTicketInstanceActions.___, data: value, target: iframe or parent }
        const message = e.data;
        if (message?.target !== 'parent') return;
        switch (message?.action) {
          case FloatingTicketInstanceActions.RequestModeChange:
            dispatch(
              editFloatingTicketPanel({
                url: message.url,
                mode: message.data,
              }),
            );
            break;
          case FloatingTicketInstanceActions.RequestLoader:
            if (!panels[message.url] || panels[message.url].isLoading) return;
            dispatch(
              toggleFloatingTicketPanelLoading({
                url: message.url,
                isLoading: true,
              }),
            );
            break;
          case FloatingTicketInstanceActions.RequestStopLoader:
            if (!panels[message.url] || panels[message.url].isLoading === false) return;
            performance.mark(PERFORMANCE_MEASURE.FLOATING_TICKET_PANEL.POPULATE_FIELDS);
            performanceMeasureEventReport(PERFORMANCE_MEASURE.EVENTS.POPULATE_NEW_TICKET_FIELDS, {
              tenantId: localStorage.getItem(APP_CONSTANTS.ACCOUNT_ID_LOCAL_KEY),
              duration:
                performance.getEntriesByName(PERFORMANCE_MEASURE.FLOATING_TICKET_PANEL.POPULATE_FIELDS)[0]?.startTime -
                performance.getEntriesByName(PERFORMANCE_MEASURE.QUEUE.CREATE_TICKET_BTN)[0]?.startTime,
            });
            dispatch(
              toggleFloatingTicketPanelLoading({
                url: message.url,
                isLoading: false,
              }),
            );
            break;
          case FloatingTicketInstanceActions.TicketCreated:
            {
              const { url, data } = message;
              queryClient.invalidateQueries({ queryKey: ['queueData'] });
              dispatch(
                editFloatingTicketPanel({
                  url,
                  mode: FloatingTicketInstanceModes.CLOSED,
                }),
              );
              if (messageCreatedText) {
                dispatch(
                  setToasterMessage({
                    message: ticketCreatedMessage(
                      `${messageCreatedText}. <span style="color:${theme?.palette?.text?.blue_user_name};text-decoration: underline;font-family:${theme?.fonts?.robotoRegular};"> ${viewSR}</span>`,
                      data.id,
                    ),
                    onClickFunctionName: 'ticketCreatedToastClicked',
                    id: data.id,
                    showFor: 6000,
                  }),
                );
              }
            }
            break;
          case FloatingTicketInstanceActions.ErrorCreatingTicket:
            if (failedToCreateTicket) {
              dispatch(
                setToasterMessage({
                  type: 'error',
                  message: failedToCreateTicket,
                }),
              );
            }
            break;

          case FloatingTicketInstanceActions.RequestClosedMessage:
            if (areYouSureTitle) {
              dispatch(
                showPrompt({
                  title: areYouSureTitle,
                  descriptionText: areYouSureBody,
                  btnOkText: areYouSureOk,
                  btnCancelText: areYouSureCancel,
                  url: message.url,
                  onClickFunctionName: closeFloatingTicketPanel.toString(),
                }),
              );
            }
            break;
          default:
            break;
        }
      }),
    [
      dispatch,
      panels,
      messageCreatedText,
      viewSR,
      failedToCreateTicket,
      areYouSureTitle,
      areYouSureBody,
      areYouSureOk,
      areYouSureCancel,
      theme?.palette?.text?.blue_user_name,
      theme?.fonts?.robotoRegular,
      queryClient,
      performanceMeasureEventReport,
    ],
  );

  useEffect(() => {
    handleMessage();
  }, [handleMessage]);

  const isFloatingTicketPanelActive = keys.length > 0;
  const onLeft = keys.length > getVisiblePanelsOnScreenCount(navBarWidth) + 1 ? keys.slice(0, startIndexToShow) : [];
  const onRight =
    keys.length - onLeft.length - getVisiblePanelsOnScreenCount(navBarWidth) > 0
      ? keys.slice(startIndexToShow + getVisiblePanelsOnScreenCount(navBarWidth))
      : [];
  const openedTicketPanelIndex = keys.findIndex((url) => panels[url].mode === FloatingTicketInstanceModes.OPENED);
  const expandedTicketPanelIndex = keys.findIndex((url) => panels[url].mode === FloatingTicketInstanceModes.EXPANDED);
  const hasExpandedTicketPanel = expandedTicketPanelIndex !== -1;

  return (
    <>
      {hasExpandedTicketPanel && <StyledFloatingTicketPanelBackdrop />}
      {onLeft.length > 0 && (
        <MoreFloatingTickets
          onClick={() => {
            if (isDisabled) return;
            onShowMoreClicked(-1);
          }}
          count={onLeft.length}
        />
      )}
      {isFloatingTicketPanelActive && (
        <>
          {keys.map((url, index) => (
            <FloatingTicketPanelInstance
              openedTicketPanelIndex={openedTicketPanelIndex}
              expandedTicketPanelIndex={expandedTicketPanelIndex}
              startIndexToShow={startIndexToShow}
              index={index}
              key={url}
              url={url}
              mode={panels[url].mode}
              totalPanels={keys.length}
              isFrameDataLoading={panels[url].isLoading}
              isRightArrowShown={onRight.length > 0}
            />
          ))}
        </>
      )}
      {onRight.length > 0 && (
        <MoreFloatingTickets
          direction="right"
          onClick={() => {
            if (isDisabled) return;
            onShowMoreClicked(+1);
          }}
          count={onRight.length}
        />
      )}
    </>
  );
}
